import { addDays, addMonths, endOfMonth, financialYearOf, parts, mkDate, startOfMonth, type ISODate } from '@keel/core';

export type DatePreset = 'all' | 'this_month' | 'last_month' | 'this_quarter' | 'last_quarter' | 'this_fy' | 'last_fy' | 'last_90' | 'custom';

export const DATE_PRESET_LABELS: Readonly<Record<DatePreset, string>> = {
  all: 'All dates', this_month: 'This month', last_month: 'Last month', this_quarter: 'This quarter', last_quarter: 'Last quarter',
  this_fy: 'This financial year', last_fy: 'Last financial year', last_90: 'Last 90 days', custom: 'Custom range',
};
export const DATE_PRESET_OPTIONS = (Object.keys(DATE_PRESET_LABELS) as DatePreset[]).map((p) => ({ value: p, label: DATE_PRESET_LABELS[p] }));

export interface DateRange { readonly from?: ISODate; readonly to?: ISODate }

const quarterStart = (d: ISODate): ISODate => {
  const p = parts(d);
  return mkDate(p.y, Math.floor((p.m - 1) / 3) * 3 + 1, 1);
};

/**
 * Inclusive date range for a preset. Financial years follow the entity's `fyStartMonth`/`fyStartDay`
 * (AU 1 July, UK 6 April …); quarters are calendar quarters (they line up with AU/NZ/US FY quarters).
 * `custom` and `all` return the given range unchanged.
 */
export const datePresetRange = (preset: DatePreset, today: ISODate, fyStartMonth = 7, fyStartDay = 1, custom: DateRange = {}): DateRange => {
  switch (preset) {
    case 'this_month': return { from: startOfMonth(today), to: endOfMonth(today) };
    case 'last_month': { const m = addMonths(startOfMonth(today), -1); return { from: m, to: endOfMonth(m) }; }
    case 'this_quarter': { const q = quarterStart(today); return { from: q, to: addDays(addMonths(q, 3), -1) }; }
    case 'last_quarter': { const q = addMonths(quarterStart(today), -3); return { from: q, to: addDays(addMonths(q, 3), -1) }; }
    case 'this_fy': { const fy = financialYearOf(today, fyStartMonth, fyStartDay); return { from: fy.start, to: fy.end }; }
    case 'last_fy': {
      const fy = financialYearOf(addDays(financialYearOf(today, fyStartMonth, fyStartDay).start, -1), fyStartMonth, fyStartDay);
      return { from: fy.start, to: fy.end };
    }
    case 'last_90': return { from: addDays(today, -89), to: today };
    case 'custom': return custom;
    default: return {};
  }
};
